import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { ScrollArea } from "../components/ui/scroll-area";
import {
  ArrowLeft, Send, MessageSquare, Phone, Mail, Users,
  Search, Megaphone, ChevronRight, User
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function MessagesPage({ user, onBack }) {
  const [messages, setMessages] = useState([]);
  const [campers, setCampers] = useState([]);
  const [msgText, setMsgText] = useState("");
  // Parents receive email only — SMS is removed for counselor/supervisor
  // outbound to parents (per camp policy). Backend also enforces this.
  const [channel] = useState("email");
  const [sending, setSending] = useState(false);
  const [search, setSearch] = useState("");
  const [activeThread, setActiveThread] = useState(null); // camper_id or "all"
  const [showCompose, setShowCompose] = useState(false);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  const fetchMessages = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API}/messages`, { headers: { Authorization: `Bearer ${token}` } });
      setMessages(res.data);
    } catch (e) { /* */ }
  }, []);

  const fetchCampers = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API}/campers`, { headers: { Authorization: `Bearer ${token}` } });
      // Keep EVERY active-session camper visible — even those without parent
      // contact info on file. (Previously these were silently dropped, which
      // meant the counselor wouldn't know they exist. Better UX: show them
      // with a "No parent contact" badge so the camp office can fix it in
      // CampMinder.) Filter logic:
      //   • Drop only if every session has already ended.
      //   • Tag each camper with `_messageable` so the UI can disable the
      //     send button + show a clear reason.
      const today = new Date().toISOString().split("T")[0];
      setCampers(res.data
        .filter(c => {
          const sessions = c.sessions || [];
          if (sessions.length === 0) return true;  // not yet synced — keep visible
          return sessions.some(s => (s.end || "9999-12-31") >= today);
        })
        .map(c => ({
          ...c,
          _messageable: !!(c.parent_info?.email || c.parent_info?.phone),
        }))
      );
    } catch (e) { /* */ }
  }, []);

  useEffect(() => {
    fetchMessages();
    fetchCampers();
    const token = localStorage.getItem("token");
    axios.post(`${API}/messages/notifications/read`, {}, { headers: { Authorization: `Bearer ${token}` } }).catch(() => {});
    const iv = setInterval(fetchMessages, 15000);
    return () => clearInterval(iv);
  }, [fetchMessages, fetchCampers]);

  useEffect(() => {
    if (bottomRef.current && activeThread) {
      bottomRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, activeThread]);

  // Opening a thread = reading it. Persist read state so badges clear.
  useEffect(() => {
    if (!activeThread || activeThread === "compose" || activeThread === "all") return;
    const hasUnread = messages.some(m => m.direction === "inbound" && m.camper_id === activeThread && !m.read);
    if (!hasUnread) return;
    const token = localStorage.getItem("token");
    axios.post(`${API}/messages/mark-read`, { camper_id: activeThread }, { headers: { Authorization: `Bearer ${token}` } }).catch(() => {});
    setMessages(prev => prev.map(m =>
      m.direction === "inbound" && m.camper_id === activeThread ? { ...m, read: true } : m
    ));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeThread, messages]);

  const handleSend = async (camperIds) => {
    if (!msgText.trim()) return toast.error("Enter a message");
    setSending(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(`${API}/messages/send`, {
        camper_ids: camperIds,
        message: msgText,
        channel
      }, { headers: { Authorization: `Bearer ${token}` } });
      const msgId = res.data.message_id;
      const total = res.data.recipient_count;
      const isBulk = !camperIds.length || camperIds.length > 1;
      // Optimistic toast — replaced by real delivery numbers once the
      // background task settles. Use a sticky toast for bulk so the
      // counselor sees LIVE PROGRESS and the FINAL result instead of
      // a silent green.
      const toastId = isBulk
        ? toast.loading(`Sending 0/${total}…`, { duration: 300000 })
        : toast.success(`Sending to ${total} parent(s)`);
      setMsgText("");
      // For bulks at ~30 msg/min, a 50-parent blast takes ~100s. Poll
      // every 2s for up to 4 minutes; stop as soon as the bg task's
      // status flips off "sending".
      let polls = 0;
      const maxPolls = 120; // 4 minutes
      const pollIntv = setInterval(async () => {
        polls += 1;
        try {
          const sRes = await axios.get(`${API}/messages/${msgId}/status`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          const s = sRes.data;
          const smsOk = s.sms_sent || 0;
          const smsFail = s.sms_failed || 0;
          const emOk = s.email_sent || 0;
          const emFail = s.email_failed || 0;
          const done = s.progress_done || 0;
          const totalDone = s.progress_total || total;
          const wantsSMS = s.channel === "sms" || s.channel === "both";
          const wantsEmail = s.channel === "email" || s.channel === "both";

          // Still sending → update the live meter
          if (s.status === "sending" && polls < maxPolls) {
            if (isBulk) {
              toast.loading(`Sending ${done}/${totalDone}… (${smsOk} SMS, ${emOk} email so far)`, {
                id: toastId, duration: 300000,
              });
            }
            return;
          }

          // Finished (or hit poll cap) — show the final summary
          clearInterval(pollIntv);
          const diag = (s.diagnostic_errors || []).join("; ");
          const summary = [
            wantsSMS ? `${smsOk} SMS${smsFail ? ` (${smsFail} failed)` : ""}` : null,
            wantsEmail ? `${emOk} email${emFail ? ` (${emFail} failed)` : ""}` : null,
          ].filter(Boolean).join(" · ");
          // Pull the failed parent names out of per_recipient_errors so the
          // counselor can SEE who didn't get the message (not just a count).
          // Error strings look like "SMS to +15165551234 (Bob Smith): <err>".
          const failedNames = (s.per_recipient_errors || [])
            .map(err => {
              const m = String(err).match(/\(([^)]+)\)/);
              return m ? m[1] : null;
            })
            .filter(Boolean);
          const uniqueFailed = [...new Set(failedNames)];
          const failedList = uniqueFailed.slice(0, 5).join(", ");
          const more = uniqueFailed.length > 5 ? ` +${uniqueFailed.length - 5} more` : "";
          if (diag) {
            toast.error(`Send failed: ${diag.slice(0, 160)}`, { id: isBulk ? toastId : undefined, duration: 12000 });
          } else if (smsFail + emFail > 0) {
            const detail = uniqueFailed.length
              ? `Sent ${summary}. Failed: ${failedList}${more}`
              : `Sent ${summary}`;
            toast.warning(detail, { id: isBulk ? toastId : undefined, duration: 15000 });
          } else if (isBulk) {
            toast.success(`Sent ${summary}`, { id: toastId, duration: 6000 });
          }
          setTimeout(fetchMessages, 1500);
        } catch (_e) {
          // Status endpoint unreachable — fall back silently.
          if (polls >= maxPolls) clearInterval(pollIntv);
        }
      }, 2000);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send");
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e, camperIds) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend(camperIds);
    }
  };

  const formatTime = (ts) => {
    const d = new Date(ts);
    const now = new Date();
    const opts = { timeZone: "America/New_York" };
    const isToday = d.toLocaleDateString("en-US", opts) === now.toLocaleDateString("en-US", opts);
    if (isToday) return d.toLocaleTimeString("en-US", { ...opts, hour: "numeric", minute: "2-digit" });
    return d.toLocaleDateString("en-US", { ...opts, month: "short", day: "numeric" }) + " " + d.toLocaleTimeString("en-US", { ...opts, hour: "numeric", minute: "2-digit" });
  };

  // Build per-parent conversation list
  const buildConversations = () => {
    const convos = {}; // keyed by camper_id

    // Process inbound messages
    messages.filter(m => m.direction === "inbound" && m.camper_id).forEach(msg => {
      const cid = msg.camper_id;
      if (!convos[cid]) convos[cid] = { camper_id: cid, messages: [], lastTime: msg.timestamp, unread: 0 };
      convos[cid].messages.push(msg);
      if (!msg.read) convos[cid].unread++;
      if (new Date(msg.timestamp) > new Date(convos[cid].lastTime)) convos[cid].lastTime = msg.timestamp;
    });

    // Process outbound messages — expand per recipient
    messages.filter(m => m.direction === "outbound" && m.recipients).forEach(msg => {
      msg.recipients.forEach(r => {
        const cid = r.camper_id;
        if (!cid) return;
        if (!convos[cid]) convos[cid] = { camper_id: cid, messages: [], lastTime: msg.timestamp, unread: 0 };
        // Avoid duplicate — check if this msg.id is already in
        if (!convos[cid].messages.find(m => m.id === msg.id)) {
          convos[cid].messages.push(msg);
        }
        if (new Date(msg.timestamp) > new Date(convos[cid].lastTime)) convos[cid].lastTime = msg.timestamp;
      });
    });

    // Enrich with camper info
    return Object.values(convos).map(c => {
      const camper = campers.find(x => x.id === c.camper_id);
      return {
        ...c,
        camperName: camper ? `${camper.first_name} ${camper.last_name}` : "Unknown",
        parentName: camper?.parent_info?.name || "",
        parentEmail: camper?.parent_info?.email || "",
        parentPhone: camper?.parent_info?.phone || "",
        lastMessage: c.messages.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))[0]
      };
    }).sort((a, b) => new Date(b.lastTime) - new Date(a.lastTime));
  };

  const conversations = buildConversations();

  // Get thread messages for a specific camper
  const getThreadMessages = (camperId) => {
    if (camperId === "all") {
      // "Message All" thread — show only outbound messages that went to everyone (no camper filter)
      return messages
        .filter(m => m.direction === "outbound" && (!m.recipients || m.recipients.length > 5))
        .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
    }
    const threadMsgs = [];
    messages.forEach(msg => {
      if (msg.direction === "inbound" && msg.camper_id === camperId) {
        threadMsgs.push(msg);
      } else if (msg.direction === "outbound" && msg.recipients) {
        if (msg.recipients.some(r => r.camper_id === camperId)) {
          threadMsgs.push(msg);
        }
      }
    });
    return threadMsgs.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
  };

  const activeCamper = activeThread ? campers.find(c => c.id === activeThread) : null;
  const activeConvo = activeThread ? getThreadMessages(activeThread) : [];

  // Filtered conversations for search
  const filteredConvos = conversations.filter(c => {
    if (!search) return true;
    const q = search.toLowerCase();
    return c.camperName.toLowerCase().includes(q) || c.parentName.toLowerCase().includes(q);
  });

  // Campers with no conversation yet (for compose)
  const campersWithoutConvo = campers.filter(c => !conversations.find(cv => cv.camper_id === c.id));
  const filteredNewCampers = campersWithoutConvo.filter(c => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (c.first_name || "").toLowerCase().includes(q) || (c.last_name || "").toLowerCase().includes(q);
  });

  // ==================== THREAD VIEW ====================
  if (activeThread && activeThread !== "compose") {
    const threadLabel = activeCamper
      ? `${activeCamper.first_name} ${activeCamper.last_name}`
      : "Conversation";
    const parentLabel = activeCamper?.parent_info?.name || activeCamper?.parent_info?.email || "";

    return (
      <div className="flex flex-col h-screen max-h-screen bg-gray-50">
        {/* Thread Header */}
        <div className="sticky top-0 z-10 bg-white border-b shadow-sm px-4 py-3">
          <div className="max-w-3xl mx-auto flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => setActiveThread(null)} data-testid="thread-back">
              <ArrowLeft className="w-4 h-4 mr-1" />Back
            </Button>
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 font-bold text-sm">
                {threadLabel.split(" ").map(w => w[0]).join("").slice(0, 2)}
              </div>
              <div>
                <h2 className="text-sm font-bold">{threadLabel}</h2>
                {parentLabel && <p className="text-xs text-muted-foreground">{parentLabel}</p>}
              </div>
            </div>
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto px-4 py-4">
          <div className="max-w-3xl mx-auto space-y-3">
            {activeConvo.length === 0 ? (
              <div className="text-center py-16 text-muted-foreground">
                <MessageSquare className="w-10 h-10 mx-auto mb-2 opacity-20" />
                <p className="text-sm">No messages yet — type below to start</p>
              </div>
            ) : (
              activeConvo.map(msg => {
                const isOutbound = msg.direction === "outbound";
                return (
                  <div key={msg.id} className={`flex ${isOutbound ? "justify-end" : "justify-start"}`} data-testid={`thread-msg-${msg.id}`}>
                    <div
                      className={`max-w-[80%] rounded-2xl px-4 py-2.5 ${
                        isOutbound
                          ? "bg-blue-600 text-white rounded-br-md"
                          : "bg-white border border-gray-200 text-gray-800 rounded-bl-md shadow-sm"
                      }`}
                    >
                      {!isOutbound && (
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-semibold text-xs">{msg.from_name || msg.from_phone || msg.from_email}</span>
                          <Badge variant="outline" className="text-[9px] px-1.5 py-0">
                            {msg.channel === "sms" ? "SMS" : "Email"}
                          </Badge>
                        </div>
                      )}
                      <p className={`text-sm ${isOutbound ? "text-white" : "text-gray-700"}`}>{msg.message}</p>
                      <div className={`flex items-center gap-2 mt-1 ${isOutbound ? "justify-end" : ""}`}>
                        <span className={`text-[10px] ${isOutbound ? "text-blue-100" : "text-muted-foreground"}`}>
                          {formatTime(msg.timestamp)}
                        </span>
                        {isOutbound && msg.status === "sent" && (
                          <span className="text-[10px] text-blue-100">Delivered</span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
            <div ref={bottomRef} />
          </div>
        </div>

        {/* Reply Bar */}
        <div className="sticky bottom-0 bg-white border-t px-4 py-3" data-testid="thread-reply-bar">
          <div className="max-w-3xl mx-auto">
            {(channel === "sms" || channel === "both") && (
              <p className="text-[10px] text-gray-400 mb-1.5" data-testid="sms-consent-notice">
                SMS recipients have consented to messages from Rolling River Day Camp. Opt-out footer appended automatically. <a href="/sms-terms" target="_blank" rel="noopener noreferrer" className="underline text-blue-500">SMS Terms</a>
              </p>
            )}
            <div className="flex items-end gap-2">
            <Textarea
              ref={inputRef}
              placeholder={`Message ${threadLabel}'s parent...`}
              value={msgText}
              onChange={(e) => setMsgText(e.target.value)}
              onKeyDown={(e) => handleKeyDown(e, [activeThread])}
              className="flex-1 min-h-[96px] max-h-[240px] text-sm resize-none"
              rows={3}
              data-testid="message-input"
            />
            <Button
              onClick={() => handleSend([activeThread])}
              disabled={sending || !msgText.trim()}
              className="h-10 bg-blue-600 hover:bg-blue-700"
              data-testid="send-btn"
            >
              {sending ? "..." : <Send className="w-4 h-4" />}
            </Button>
          </div>
          </div>
        </div>
      </div>
    );
  }

  // ==================== INBOX VIEW ====================
  return (
    <div className="flex flex-col h-screen max-h-screen bg-gray-50">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-white border-b shadow-sm px-4 py-3">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={onBack} className="gap-1" data-testid="messages-back">
              <ArrowLeft className="w-4 h-4" />
              <span className="text-xs font-medium">Dashboard</span>
            </Button>
            <div>
              <h1 className="text-lg font-bold flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-blue-600" />
                {user?.group_name || user?.group_code}
              </h1>
              <p className="text-xs text-muted-foreground">{campers.length} camper families</p>            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowCompose(true)}
              className="text-xs"
              data-testid="new-msg-btn"
            >
              <Send className="w-3.5 h-3.5 mr-1" />New Message
            </Button>
            <Button
              size="sm"
              onClick={() => {
                handleSend([]);
              }}
              className="text-xs bg-blue-600 hover:bg-blue-700 hidden"
              data-testid="send-all-btn"
            >
              <Megaphone className="w-3.5 h-3.5 mr-1" />Message All
            </Button>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="bg-white border-b px-4 py-2">
        <div className="max-w-3xl mx-auto relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search by camper or parent name..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 h-9 text-sm"
            data-testid="inbox-search"
          />
        </div>
      </div>

      {/* New Message Compose Modal */}
      {showCompose && (
        <div className="bg-blue-50 border-b px-4 py-3" data-testid="compose-section">
          <div className="max-w-3xl mx-auto space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Select a camper to message their parent:</span>
              <Button variant="ghost" size="sm" className="text-xs" onClick={() => setShowCompose(false)}>Cancel</Button>
            </div>
            <ScrollArea className="h-32">
              <div className="flex flex-wrap gap-1.5">
                {campers.map(c => (
                  <button
                    key={c.id}
                    onClick={() => { setActiveThread(c.id); setShowCompose(false); }}
                    disabled={!c._messageable}
                    title={c._messageable ? "" : "No parent email/phone on file — update in CampMinder"}
                    className={
                      c._messageable
                        ? "px-2.5 py-1.5 rounded-full text-xs font-medium bg-white border border-gray-200 hover:border-blue-400 hover:bg-blue-50 transition-all"
                        : "px-2.5 py-1.5 rounded-full text-xs font-medium bg-gray-100 text-gray-400 border border-gray-200 cursor-not-allowed line-through"
                    }
                    data-testid={`compose-camper-${c.id}`}
                  >
                    {c.first_name} {c.last_name}
                    {!c._messageable && <span className="ml-1 not-italic no-underline">· no contact</span>}
                  </button>
                ))}
              </div>
            </ScrollArea>
            {/* Message All option */}
            <Button
              variant="outline"
              size="sm"
              className="w-full text-xs"
              onClick={() => {
                setShowCompose(false);
                // Send to all messageable — open a special compose
                setActiveThread("compose-all");
              }}
              data-testid="compose-all-btn"
            >
              <Megaphone className="w-3.5 h-3.5 mr-1" />Message All {campers.filter(c => c._messageable).length} Families
            </Button>
          </div>
        </div>
      )}

      {/* Message All Compose */}
      {activeThread === "compose-all" && (
        <div className="bg-blue-50 border-b px-4 py-3" data-testid="compose-all-section">
          <div className="max-w-3xl mx-auto space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium flex items-center gap-2">
                <Megaphone className="w-4 h-4 text-blue-600" />
                Message All {campers.filter(c => c._messageable).length} Families
              </span>
              <Button variant="ghost" size="sm" className="text-xs" onClick={() => setActiveThread(null)}>Cancel</Button>
            </div>
            <div className="flex items-end gap-2">
              <Textarea
                placeholder={`Message all ${campers.filter(c => c._messageable).length} families...`}
                value={msgText}
                onChange={(e) => setMsgText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    handleSend([]);
                  }
                }}
                className="flex-1 min-h-[96px] max-h-[240px] text-sm resize-none"
                rows={3}
                data-testid="message-input-all"
              />
              <Button
                onClick={() => handleSend([])}
                disabled={sending || !msgText.trim()}
                className="h-10 bg-blue-600 hover:bg-blue-700"
                data-testid="send-all-btn"
              >
                {sending ? "..." : <Send className="w-4 h-4" />}
              </Button>
            </div>
            {(channel === "sms" || channel === "both") && (
              <p className="text-[10px] text-gray-400 mt-1.5" data-testid="sms-consent-notice-all">
                SMS recipients have consented to messages from Rolling River Day Camp. Opt-out footer appended automatically. <a href="/sms-terms" target="_blank" rel="noopener noreferrer" className="underline text-blue-500">SMS Terms</a>
              </p>
            )}
          </div>
        </div>
      )}

      {/* Conversation List */}
      <ScrollArea className="flex-1">
        <div className="max-w-3xl mx-auto divide-y">
          {filteredConvos.length === 0 && filteredNewCampers.length === 0 && (
            <div className="text-center py-16 text-muted-foreground">
              <MessageSquare className="w-12 h-12 mx-auto mb-3 opacity-20" />
              <p className="text-sm font-medium">No conversations yet</p>
              <p className="text-xs mt-1">Click "New Message" to start a conversation with a parent</p>
            </div>
          )}

          {filteredConvos.map(convo => {
            const lastMsg = convo.lastMessage;
            const isInbound = lastMsg?.direction === "inbound";
            const preview = lastMsg?.message?.slice(0, 80) || "";
            return (
              <button
                key={convo.camper_id}
                onClick={() => setActiveThread(convo.camper_id)}
                className="w-full px-4 py-3 flex items-center gap-3 hover:bg-gray-50 transition-colors text-left"
                data-testid={`convo-${convo.camper_id}`}
              >
                {/* Avatar */}
                <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0 ${
                  convo.unread > 0 ? "bg-blue-600 text-white" : "bg-gray-100 text-gray-600"
                }`}>
                  {convo.camperName.split(" ").map(w => w[0]).join("").slice(0, 2)}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <span className={`text-sm truncate ${convo.unread > 0 ? "font-bold" : "font-medium"}`}>
                      {convo.camperName}
                    </span>
                    <span className="text-[10px] text-muted-foreground flex-shrink-0 ml-2">
                      {formatTime(convo.lastTime)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between mt-0.5">
                    <p className={`text-xs truncate ${convo.unread > 0 ? "text-gray-800 font-medium" : "text-muted-foreground"}`}>
                      {isInbound ? "" : "You: "}{preview}
                    </p>
                    <div className="flex items-center gap-1.5 flex-shrink-0 ml-2">
                      {convo.unread > 0 && (
                        <span className="bg-blue-600 text-white text-[10px] w-5 h-5 rounded-full flex items-center justify-center font-bold">
                          {convo.unread}
                        </span>
                      )}
                      <ChevronRight className="w-4 h-4 text-muted-foreground" />
                    </div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </ScrollArea>
    </div>
  );
}
